import React, { useEffect, useMemo, useState } from 'react';
import { useTable, useGlobalFilter, useSortBy, usePagination } from 'react-table';
import 'bootstrap/dist/css/bootstrap.min.css';
import { useParams, useNavigate } from 'react-router-dom';
import { Container, Card, Row, Col, Button, Spinner, Form, InputGroup } from 'react-bootstrap';
import './DonorProfile.css';
import { DonationService } from '../../../../../Services/Axios/Api';
import { toast } from 'react-toastify';
import { faSearch } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

const formatDateTime = (value) => {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;
    const pad = (n) => String(n).padStart(2, '0');
    const day = pad(date.getDate());
    const month = pad(date.getMonth() + 1);
    const year = date.getFullYear();
    const hours = pad(date.getHours());
    const minutes = pad(date.getMinutes());
    const seconds = pad(date.getSeconds());
    return `${day}/${month}/${year} ${hours}:${minutes}:${seconds}`;
};

const formatAmountWithCurrency = (amount, currency) => {
    const num = typeof amount === 'number' ? amount : Number(amount);
    const formattedAmount = Number.isFinite(num) ? num.toLocaleString() : amount;
    return currency ? `${formattedAmount} ${currency}` : `${formattedAmount}`;
};

const getPaymentMethodVariant = (method) => {
    const key = String(method || '').toLowerCase();
    switch (key) {
        case 'paypal':
            return 'info';
        case 'fawry':
            return 'warning';
        case 'noon':
            return 'primary';
        case 'ahlymomken':
        case 'momken':
            return 'secondary';
        default:
            return 'dark';
    }
};

const DonorProfile = () => {
    const { email } = useParams();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [donationData, setDonationData] = useState(null);

    useEffect(() => {
        const fetchDonorData = async () => {
            try {
                setLoading(true);
                const response = await DonationService.getDonorStatistics(email);
                if (response.isSuccess && response.result) {
                    setDonationData(response.result);
                } else {
                    toast.error('No donations found for this donor');
                    navigate('/bj-admin/donations/donors');
                }
            } catch (error) {
                toast.error(error.message || 'Failed to load donor data');
                navigate('/bj-admin/donations/donors');
            } finally {
                setLoading(false);
            }
        };
        fetchDonorData();
    }, [email, navigate]);

    const donationHistory = useMemo(() => donationData?.donationHistory || [], [donationData]);

    const columns = useMemo(
        () => [
            {
                Header: 'Transaction ID',
                accessor: 'transactionId',
            },
            {
                Header: 'Campaign Name',
                accessor: 'campaignName',
            },
            {
                Header: 'Amount',
                accessor: 'amount',
                Cell: ({ row }) => {
                    const { amount, currency } = row.original;
                    return formatAmountWithCurrency(amount, currency);
                },
            },
            {
                Header: 'Payment Method',
                accessor: 'paymentMethod',
                Cell: ({ value }) => (
                    <span className={`badge bg-${getPaymentMethodVariant(value)}`}>
                        {value || '-'}
                    </span>
                ),
            },
            {
                Header: 'Date',
                accessor: 'donationDate',
                Cell: ({ value }) => formatDateTime(value),
            },
            {
                Header: 'Status',
                accessor: 'paymentStatus',
                Cell: ({ value }) => (
                    <span className={`donation-status-badge ${value.toLowerCase()}`}>
                        {value}
                    </span>
                ),
            },
            
        ],
        []
    );

    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        page,
        prepareRow,
        state,
        setGlobalFilter,
        pageOptions,
        canPreviousPage,
        canNextPage,
        nextPage,
        previousPage,
        gotoPage,
        setPageSize,
    } = useTable(
        {
            columns,
            data: donationHistory,
            initialState: {
                sortBy: [{ id: 'donationDate', desc: true }],
                pageSize: 10,
            },
        },
        useGlobalFilter,
        useSortBy,
        usePagination
    );

    const { globalFilter, pageIndex, pageSize } = state;

    if (loading) {
        return (
            <Container className="donor-profile-container">
                <div className="text-center mt-5">
                    <Spinner animation="border" variant="primary" />
                </div>
            </Container>
        );
    }

    return (
        <Container className="donor-profile-container">
            <Button 
                variant="outline-secondary" 
                className="mb-4" 
                onClick={() => navigate('/bj-admin/donations/donors')}
            >
                Back to Donors
            </Button>

            <Card className="donor-info-card mb-4">
                <Card.Body>
                    <Row>
                        <Col md={6}>
                            <h4 className="donor-info-title">Donor Information</h4>
                            <div className="donor-info-item">
                                <span className="donor-info-label">Email:</span>
                                <span className="donor-info-value">{email}</span>
                            </div>
                        </Col>
                        <Col md={6}>
                            <h4 className="donor-info-title">Donation Summary</h4>
                            <div className="donor-info-item">
                                <span className="donor-info-label">Total Donations:</span>
                                <span className="donor-info-value">{donationData?.totalDonations}</span>
                            </div>
                            <div className="donor-info-item">
                                <span className="donor-info-label">Completed Donations Amount:</span>
                                <span className="donor-info-value">
                                   {formatAmountWithCurrency((donationData?.completedDonationsAmount ?? 0).toFixed(2), donationHistory?.[0]?.currency || '')}
                                </span>
                            </div>
                            <div className="donor-info-item">
                                <span className="donor-info-label">Last Donation:</span>
                                <span className="donor-info-value">
                                    { donationData?.lastDonationDate ? formatDateTime(donationData.lastDonationDate) : '-'}
                                </span>
                            </div>
                        </Col>
                    </Row>
                </Card.Body>
            </Card>

            <Card>
                <Card.Body>
                    <div className="d-flex flex-column flex-lg-row justify-content-between align-items-lg-center gap-3 mb-4">
                        <h4 className="donor-info-title m-0">Donation History</h4>
                        <div className="flex-grow-1" style={{ maxWidth: 420 }}>
                            {/* <Form.Label className="mb-1 small text-muted">Search</Form.Label> */}
                            <InputGroup size="sm" className="bg-white rounded-2 shadow-sm">
                                <InputGroup.Text className="bg-white border-0 text-primary">
                                    <FontAwesomeIcon icon={faSearch} />
                                </InputGroup.Text>
                                <Form.Control
                                    type="search"
                                    placeholder="Search donations..."
                                    value={globalFilter || ''}
                                    onChange={e => {
                                        setGlobalFilter(e.target.value);
                                        gotoPage(0);
                                    }}
                                    className="border-0"
                                />
                            </InputGroup>
                        </div>
                    </div>
                    <div className="donor-table-container">
                        <table className="table table-hover" {...getTableProps()}>
                            <thead className="donor-table-header">
                                {headerGroups.map(headerGroup => {
                                    const { key, ...restHeaderGroupProps } = headerGroup.getHeaderGroupProps();
                                    return (
                                        <tr key={key} {...restHeaderGroupProps}>
                                            {headerGroup.headers.map(column => {
                                                const { key: columnKey, ...restHeaderProps } = column.getHeaderProps(column.getSortByToggleProps());
                                                return (
                                                    <th 
                                                        key={columnKey}
                                                        className="donor-table-th" 
                                                        {...restHeaderProps}
                                                    >
                                                        {column.render('Header')}
                                                        <span className="donor-sort-indicator">
                                                            {column.isSorted
                                                                ? column.isSortedDesc
                                                                    ? ' 🔽'
                                                                    : ' 🔼'
                                                                : ''}
                                                        </span>
                                                    </th>
                                                );
                                            })}
                                        </tr>
                                    );
                                })}
                            </thead>
                            <tbody {...getTableBodyProps()}>
                                {page.map(row => {
                                    prepareRow(row);
                                    const { key, ...restRowProps } = row.getRowProps();
                                    return (
                                        <tr key={key} {...restRowProps}>
                                            {row.cells.map(cell => {
                                                const { key: cellKey, ...restCellProps } = cell.getCellProps();
                                                return (
                                                    <td key={cellKey} {...restCellProps}>
                                                        {cell.render('Cell')}
                                                    </td>
                                                );
                                            })}
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>

                    <div className="d-flex flex-column flex-md-row align-items-center justify-content-between mt-3 gap-2">
                        <div className="d-flex align-items-center gap-2">
                            <span>Rows per page:</span>
                            <Form.Select
                                size="sm"
                                style={{ width: '90px' }}
                                value={pageSize}
                                onChange={(e) => setPageSize(Number(e.target.value))}
                            >
                                {[10, 20, 50, 100].map(size => (
                                    <option key={size} value={size}>{size}</option>
                                ))}
                            </Form.Select>
                        </div>
                        <div className="d-flex align-items-center gap-2">
                            <Button variant="outline-primary" size="sm" onClick={() => gotoPage(0)} disabled={!canPreviousPage}>
                                {'<<'}
                            </Button>
                            <Button variant="outline-primary" size="sm" onClick={() => previousPage()} disabled={!canPreviousPage}>
                                {'<'}
                            </Button>
                            <span className="mx-2">
                                Page <strong>{pageIndex + 1}</strong> of <strong>{pageOptions.length}</strong>
                            </span>
                            <Form.Control
                                size="sm"
                                type="number"
                                min={1}
                                max={pageOptions.length}
                                value={pageIndex + 1}
                                onChange={(e) => {
                                    const next = e.target.value ? Number(e.target.value) - 1 : 0;
                                    gotoPage(next);
                                }}
                                style={{ width: '80px' }}
                            />
                            <Button variant="outline-primary" size="sm" onClick={() => nextPage()} disabled={!canNextPage}>
                                {'>'}
                            </Button>
                            <Button variant="outline-primary" size="sm" onClick={() => gotoPage(pageOptions.length - 1)} disabled={!canNextPage}>
                                {'>>'}
                            </Button>
                        </div>
                    </div>
                </Card.Body>
            </Card>
        </Container>
    );
};

export default DonorProfile;
